import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  Box,
  Typography,
  TextField,
  Stack,
  Button,
  Chip,
  Divider,
  Select,
  MenuItem,
  InputAdornment,
  CircularProgress,
  Switch,
  FormControlLabel,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LanguageIcon from "@mui/icons-material/Language";
import TravelExploreIcon from "@mui/icons-material/TravelExplore";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import DnsOutlinedIcon from "@mui/icons-material/DnsOutlined";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import ErrorOutlinedIcon from "@mui/icons-material/ErrorOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import DashboardLayout from "../layouts/DashboardLayout";
import { useThemeColors } from "../context/ThemeContext";
import { ListSkeleton } from "../components/TableSkeleton";
import { useToast } from "../components/ToastProvider";
import api from "../api";
import {
  useDomain,
  useUpdateDomain,
  useCategories,
  useServers,
} from "../hooks/queries";
import { HEADING_FONT } from "../theme/fonts";
import ClientDetailsPanel from "../components/ClientDetailsPanel";
import InlineAddServer from "../components/InlineAddServer";

// Status bands — mirror the worker (<0 = Expired, ≤30 = Near Expiry, else Active).
const STATUS_COLOR = {
  Active: "#16A34A",
  "Near Expiry": "#D97706",
  Expired: "#DC2626",
  New: "#3B82F6",
};
function daysLeft(expiry) {
  if (!expiry) return null;
  const d = new Date(expiry);
  if (isNaN(d.getTime())) return null;
  return Math.ceil((d - new Date()) / (1000 * 60 * 60 * 24));
}
function computeStatus(expiry, fallback) {
  const days = daysLeft(expiry);
  if (days === null) return fallback || "New";
  if (days < 0) return "Expired";
  if (days <= 30) return "Near Expiry";
  return "Active";
}
const toDate = (v) => (v ? String(v).slice(0, 10) : "");

// Section heading — same look as the Domain Details (view) page.
function SectionHeader({ icon, title, colors }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: 1.5,
          background:
            "linear-gradient(135deg, rgba(59, 130, 246, 0.18) 0%, rgba(59, 130, 246, 0.18) 100%)",
          color: "#3B82F6",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {icon}
      </Box>
      <Box
        sx={{
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        <Typography
          sx={{
            fontFamily: HEADING_FONT,
            fontSize: 14,
            fontWeight: 700,
            color: colors.text,
            letterSpacing: "-0.2px",
            lineHeight: 1.2,
            mt: "2px",
          }}
        >
          {title}
        </Typography>
      </Box>
    </Stack>
  );
}

function EditDomain() {
  const { id } = useParams();
  const navigate = useNavigate();
  const colors = useThemeColors();
  const toast = useToast();
  const queryClient = useQueryClient();

  const domainQuery = useDomain(id);
  const updateDomain = useUpdateDomain();
  const { data: categoriesData } = useCategories();
  const categories = (categoriesData || []).map((c) => c.name).filter(Boolean);

  // Server names for the "Server Name" dropdown (from the Servers page).
  const { data: serversData } = useServers();
  const serversList = Array.isArray(serversData) ? serversData : serversData?.rows || [];

  const [form, setForm] = useState(null);
  const [fetching, setFetching] = useState(false);
  const [saving, setSaving] = useState(false);
  // Inline "Add New Server" mini-form under the Server dropdown.
  const [showAddServer, setShowAddServer] = useState(false);

  // Prefill the form once the domain loads.
  useEffect(() => {
    const d = domainQuery.data;
    if (!d) return;
    setForm({
      ...d,
      expiry: toDate(d.expiry),
      registered: toDate(d.registered),
      nameServersText: Array.isArray(d.nameServers)
        ? d.nameServers.join(", ")
        : d.nameServers || "",
    });
  }, [domainQuery.data]);

  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  // Keep the domain's current server value selectable even if that server is no
  // longer in the list (legacy free-text values) — so nothing silently clears.
  // Separate hosting = a client-owned server; normal = a self server. Filter the
  // dropdown to match the toggle, but always keep the domain's current server
  // selectable so nothing silently clears.
  const serverNamesForType = serversList
    .filter((s) =>
      form?.hasSeparateHosting ? s.ownership === "client" : s.ownership !== "client"
    )
    .map((s) => s.name)
    .filter(Boolean);
  const serverOptions =
    form?.serverName && !serverNamesForType.includes(form.serverName)
      ? [form.serverName, ...serverNamesForType]
      : serverNamesForType;

  // IP is read-only now — it comes from the linked server's public IP, not the
  // domain. Falls back to the value the domain loaded with for legacy servers
  // that aren't in the current list.
  const selectedServer = serversList.find((s) => s.name === form?.serverName);
  const serverIp = selectedServer?.publicIp || form?.ip || "";

  // A server just added via the inline mini-form: select it by name and show its
  // IP right away, then refresh the servers list so the dropdown includes it.
  function handleServerCreated(server) {
    setForm((prev) => ({
      ...prev,
      serverName: server.name,
      ip: server.publicIp || "",
    }));
    setShowAddServer(false);
    queryClient.invalidateQueries({ queryKey: ["servers"] });
  }

  // Auto-fill from WHOIS/RDAP + DNS + SSL — only overwrites resolved fields.
  async function handleFetch() {
    const name = (form?.name || "").trim();
    if (!name || fetching) return;
    setFetching(true);
    try {
      const info = await api.get(
        `/domains/lookup?name=${encodeURIComponent(name)}`
      );
      setForm((prev) => {
        const next = { ...prev };
        if (info.registrar) next.registrar = info.registrar;
        if (info.registered) next.registered = toDate(info.registered);
        if (info.expiry) next.expiry = toDate(info.expiry);
        if (Array.isArray(info.nameServers) && info.nameServers.length) {
          next.nameServers = info.nameServers;
          next.nameServersText = info.nameServers.join(", ");
        }
        if (info.sslValidity !== null && info.sslValidity !== undefined) {
          next.sslValidity = info.sslValidity;
        }
        return next;
      });
      if (info.found || info.ip) {
        toast.success("Details fetched from WHOIS/DNS");
      } else {
        toast.warning(
          "Couldn't find registry details for this domain. Check the spelling, or fill them in manually."
        );
      }
    } catch (err) {
      toast.error(err.message || "Lookup failed. Please try again.");
    } finally {
      setFetching(false);
    }
  }

  async function handleSave() {
    if (!form?.name?.trim()) {
      toast.error("Domain name is required");
      return;
    }
    const status = computeStatus(form.expiry, form.status);
    const nameServers = String(form.nameServersText ?? "")
      .split(/[\n,]/)
      .map((s) => s.trim())
      .filter(Boolean);
    setSaving(true);
    try {
      await updateDomain.mutateAsync({
        id: form.id,
        data: { ...form, status, nameServers },
      });
      toast.success("Domain updated successfully");
      navigate("/domains");
    } catch (err) {
      toast.error(err.message || "Failed to update domain");
    } finally {
      setSaving(false);
    }
  }

  const labelSx = {
    display: "block",
    fontSize: 12,
    fontWeight: 600,
    color: colors.textMuted,
    mb: 0.75,
  };
  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      background: colors.cardBg,
      color: colors.text,
      borderRadius: 2,
      "& fieldset": { borderColor: colors.cardBorder },
      "&:hover fieldset": { borderColor: colors.cardBorderHover },
      "&.Mui-focused fieldset": { borderColor: "#3B82F6" },
    },
    "& input": { color: colors.text },
    "& textarea": { color: colors.text },
  };
  // Glass card — identical recipe to the Domain Details (view) page.
  const cardSx = {
    background: "#ffffff",
    border: "1px solid #EEF1F6",
    borderRadius: "16px",
    boxShadow: "0 1px 3px rgba(16, 24, 40, 0.05)",
    p: 3,
  };
  const gridSx = {
    display: "grid",
    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
    gap: 2,
  };

  // Loading / not-found states.
  if (domainQuery.isLoading || (!form && !domainQuery.isError)) {
    return (
      <DashboardLayout>
        <ListSkeleton rows={6} />
      </DashboardLayout>
    );
  }
  if (domainQuery.isError || !form) {
    return (
      <DashboardLayout>
        <Box sx={{ ...cardSx, py: 8, textAlign: "center" }}>
          <Typography sx={{ fontFamily: HEADING_FONT, fontSize: 16, fontWeight: 700, color: colors.text }}>
            Domain not found
          </Typography>
          <Button
            onClick={() => navigate("/domains")}
            startIcon={<ArrowBackIcon />}
            sx={{ mt: 2, textTransform: "none", color: "#3B82F6" }}
          >
            Back to Domains
          </Button>
        </Box>
      </DashboardLayout>
    );
  }

  const editStatus = computeStatus(form.expiry, form.status);
  const statusColor = STATUS_COLOR[editStatus] || STATUS_COLOR.Active;
  const StatusIcon =
    editStatus === "Expired"
      ? ErrorOutlinedIcon
      : editStatus === "Near Expiry"
      ? WarningAmberOutlinedIcon
      : CheckCircleOutlinedIcon;

  return (
    <DashboardLayout>
      {/* Back link */}
      <Button
        onClick={() => navigate("/domains")}
        startIcon={<ArrowBackIcon />}
        sx={{
          textTransform: "none",
          fontWeight: 600,
          color: colors.textMuted,
          mb: 2,
          ml: -1,
          "&:hover": { background: colors.hover, color: colors.text },
        }}
      >
        Back to Domains
      </Button>

      {/* Hero card */}
      <Box
        sx={{
          position: "relative",
          background:
            "linear-gradient(135deg, #3B82F6 0%, #3B82F6 50%, #60A5FA 100%)",
          borderRadius: 3,
          p: { xs: 3, md: 4 },
          mb: 3,
          overflow: "hidden",
          color: "#fff",
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={3}
          alignItems={{ md: "center" }}
          justifyContent="space-between"
          sx={{ position: "relative", zIndex: 1 }}
        >
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{ minWidth: 0, flex: 1 }}
          >
            <Box
              sx={{
                width: 60,
                height: 60,
                borderRadius: 2,
                background: "rgba(255,255,255,0.18)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <LanguageIcon sx={{ fontSize: 32 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 600,
                  letterSpacing: "1.5px",
                  textTransform: "uppercase",
                  opacity: 0.85,
                }}
              >
                Edit Domain
              </Typography>
              <Typography
                sx={{
                  fontFamily: HEADING_FONT,
                  fontSize: { xs: 24, md: 32 },
                  fontWeight: 700,
                  letterSpacing: "-0.5px",
                  lineHeight: 1.1,
                  wordBreak: "break-word",
                }}
              >
                {form.name || "(unnamed)"}
              </Typography>
              {form.registrar && (
                <Typography sx={{ fontSize: 13, opacity: 0.85, mt: 0.5 }}>
                  Registered via {form.registrar}
                </Typography>
              )}
            </Box>
          </Stack>

          <Chip
            icon={
              <StatusIcon sx={{ fontSize: 16, color: "#fff !important" }} />
            }
            label={editStatus}
            sx={{
              fontWeight: 700,
              fontSize: 12,
              color: "#fff",
              background: "rgba(255,255,255,0.22)",
              border: "1px solid rgba(255,255,255,0.3)",
            }}
          />
        </Stack>
      </Box>

      {/* Form cards */}
      <Stack spacing={3}>
        {/* Domain & Registry */}
        <Box sx={cardSx}>
          <SectionHeader
            icon={<LanguageIcon sx={{ fontSize: 20 }} />}
            title="Domain & Registry"
            colors={colors}
          />
          <Divider sx={{ borderColor: colors.divider, mb: 2.5 }} />
          <Stack spacing={2.5}>
            <Box>
              <Typography component="label" sx={labelSx}>
                Domain Name
              </Typography>
              <TextField
                fullWidth
                value={form.name || ""}
                onChange={(e) => set("name", e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleFetch();
                  }
                }}
                sx={fieldSx}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <Button
                          onClick={handleFetch}
                          disabled={fetching || !form.name?.trim()}
                          variant="contained"
                          disableElevation
                          startIcon={
                            fetching ? (
                              <CircularProgress size={15} sx={{ color: "#fff" }} />
                            ) : (
                              <TravelExploreIcon sx={{ fontSize: 18 }} />
                            )
                          }
                          sx={{
                            flexShrink: 0,
                            textTransform: "none",
                            fontWeight: 600,
                            fontSize: 13,
                            borderRadius: 1.5,
                            px: 1.75,
                            whiteSpace: "nowrap",
                            color: "#fff",
                            background:
                              "linear-gradient(135deg, #3B82F6 0%, #3B82F6 100%)",
                            "&:hover": {
                              background:
                                "linear-gradient(135deg, #1D4ED8 0%, #1D4ED8 100%)",
                            },
                            "&.Mui-disabled": {
                              color: "rgba(255,255,255,0.65)",
                              background:
                                "linear-gradient(135deg, rgba(59,130,246,0.55) 0%, rgba(59,130,246,0.55) 100%)",
                            },
                          }}
                        >
                          {fetching ? "Fetching…" : "Fetch"}
                        </Button>
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </Box>
            <Box sx={gridSx}>
              <Box>
                <Typography component="label" sx={labelSx}>
                  Registrar
                </Typography>
                <TextField
                  fullWidth
                  value={form.registrar || ""}
                  onChange={(e) => set("registrar", e.target.value)}
                  sx={fieldSx}
                />
              </Box>
              <Box>
                <Typography component="label" sx={labelSx}>
                  Category
                </Typography>
                <Select
                  fullWidth
                  size="small"
                  displayEmpty
                  value={form.category || ""}
                  onChange={(e) => set("category", e.target.value)}
                  sx={{
                    ...fieldSx,
                    color: colors.text,
                    background: colors.cardBg,
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: colors.cardBorder,
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: colors.cardBorderHover,
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#3B82F6",
                    },
                    "& .MuiSvgIcon-root": { color: colors.textMuted },
                  }}
                  MenuProps={{
                    slotProps: {
                      paper: {
                        sx: {
                          background: colors.menuBg,
                          border: `1px solid ${colors.cardBorder}`,
                        },
                      },
                    },
                  }}
                >
                  <MenuItem value="" sx={{ fontSize: 13, color: colors.textMuted }}>
                    — None —
                  </MenuItem>
                  {categories.map((c) => (
                    <MenuItem
                      key={c}
                      value={c}
                      sx={{ fontSize: 13, color: colors.text }}
                    >
                      {c}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            </Box>
            <Box>
              <Typography component="label" sx={labelSx}>
                Nameservers
              </Typography>
              <TextField
                fullWidth
                multiline
                minRows={2}
                placeholder="ns1.example.com, ns2.example.com"
                value={form.nameServersText || ""}
                onChange={(e) => set("nameServersText", e.target.value)}
                helperText="Separate multiple with commas or new lines"
                sx={fieldSx}
                FormHelperTextProps={{
                  sx: { color: colors.textFaint, mx: 0, mt: 0.5 },
                }}
              />
            </Box>
          </Stack>
        </Box>

        {/* Client */}
        <Box sx={cardSx}>
          <SectionHeader
            icon={<PersonOutlineOutlinedIcon sx={{ fontSize: 20 }} />}
            title="Client"
            colors={colors}
          />
          <Divider sx={{ borderColor: colors.divider, mb: 2.5 }} />
          <ClientDetailsPanel
            value={form.clientCode}
            onChange={(code) => set("clientCode", code)}
            colors={colors}
            fieldSx={fieldSx}
            labelSx={labelSx}
            source="local"
            clientId={form.clientId}
          />
        </Box>

        {/* Server & Hosting */}
        <Box sx={cardSx}>
          <SectionHeader
            icon={<DnsOutlinedIcon sx={{ fontSize: 20 }} />}
            title="Server & Hosting"
            colors={colors}
          />
          <Divider sx={{ borderColor: colors.divider, mb: 2.5 }} />
          <Stack spacing={2.5}>
            <Box sx={gridSx}>
              <Box>
                <Typography component="label" sx={labelSx}>
                  Server Name
                </Typography>
                <Select
                  fullWidth
                  size="small"
                  displayEmpty
                  value={form.serverName || ""}
                  onChange={(e) => {
                    const val = e.target.value;
                    // "+ Add New Server" opens the inline mini-form instead of
                    // storing the sentinel as the server name.
                    if (val === "__add_new__") {
                      setShowAddServer(true);
                      return;
                    }
                    set("serverName", val);
                  }}
                  sx={{
                    ...fieldSx,
                    color: colors.text,
                    background: colors.cardBg,
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: colors.cardBorder,
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: colors.cardBorderHover,
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#3B82F6",
                    },
                    "& .MuiSvgIcon-root": { color: colors.textMuted },
                  }}
                  MenuProps={{
                    slotProps: {
                      paper: {
                        sx: {
                          background: colors.menuBg,
                          border: `1px solid ${colors.cardBorder}`,
                        },
                      },
                    },
                  }}
                >
                  <MenuItem value="" sx={{ fontSize: 13, color: colors.textMuted }}>
                    — None —
                  </MenuItem>
                  {serverOptions.map((name) => (
                    <MenuItem
                      key={name}
                      value={name}
                      sx={{ fontSize: 13, color: colors.text }}
                    >
                      {name}
                    </MenuItem>
                  ))}
                  <MenuItem
                    value="__add_new__"
                    sx={{ fontSize: 13, fontWeight: 600, color: "#3B82F6" }}
                  >
                    + Add New Server
                  </MenuItem>
                </Select>
                {showAddServer && (
                  <InlineAddServer
                    ownership={form.hasSeparateHosting ? "client" : "self"}
                    onCreated={handleServerCreated}
                    onCancel={() => setShowAddServer(false)}
                    fieldSx={fieldSx}
                    labelSx={labelSx}
                  />
                )}
              </Box>
              <Box sx={{ display: showAddServer ? "none" : "block" }}>
                <Typography component="label" sx={labelSx}>
                  IP Address
                </Typography>
                <TextField
                  fullWidth
                  value={serverIp}
                  placeholder="Auto-filled from the selected server"
                  InputProps={{ readOnly: true }}
                  sx={fieldSx}
                />
              </Box>
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={!!form.hasSeparateHosting}
                  onChange={(e) => set("hasSeparateHosting", e.target.checked)}
                />
              }
              label="Separate hosting"
              sx={{
                "& .MuiFormControlLabel-label": {
                  fontSize: 13,
                  fontWeight: 600,
                  color: colors.text,
                },
              }}
            />
            {form.hasSeparateHosting && (
              <Typography sx={{ fontSize: 12, color: colors.textMuted }}>
                Only client-owned servers are shown above. Add new ones on the Servers page.
              </Typography>
            )}
          </Stack>
        </Box>

        {/* Dates & Status */}
        <Box sx={cardSx}>
          <SectionHeader
            icon={<CalendarTodayOutlinedIcon sx={{ fontSize: 20 }} />}
            title="Dates & Status"
            colors={colors}
          />
          <Divider sx={{ borderColor: colors.divider, mb: 2.5 }} />
          <Stack spacing={2.5}>
            <Box sx={gridSx}>
              <Box>
                <Typography component="label" sx={labelSx}>
                  Registered Date
                </Typography>
                <TextField
                  type="date"
                  fullWidth
                  value={form.registered || ""}
                  onChange={(e) => set("registered", e.target.value)}
                  sx={fieldSx}
                />
              </Box>
              <Box>
                <Typography component="label" sx={labelSx}>
                  Expiry Date
                </Typography>
                <TextField
                  type="date"
                  fullWidth
                  value={form.expiry || ""}
                  onChange={(e) => set("expiry", e.target.value)}
                  sx={fieldSx}
                />
              </Box>
            </Box>
            <Box sx={gridSx}>
              <Box>
                <Typography component="label" sx={labelSx}>
                  SSL Validity (days)
                </Typography>
                <TextField
                  type="number"
                  fullWidth
                  placeholder="e.g. 90"
                  value={form.sslValidity ?? ""}
                  onChange={(e) => set("sslValidity", e.target.value)}
                  sx={fieldSx}
                />
              </Box>
              <Box>
                <Typography component="label" sx={labelSx}>
                  Status
                </Typography>
                <Box
                  sx={{
                    height: 40,
                    px: 1.5,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    borderRadius: 2,
                    border: `1px solid ${colors.cardBorder}`,
                    background: colors.cardBg,
                  }}
                >
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      background: statusColor,
                    }}
                  />
                  <Typography
                    sx={{ fontSize: 13, fontWeight: 700, color: statusColor }}
                  >
                    {editStatus}
                  </Typography>
                </Box>
                <Typography
                  sx={{ fontSize: 11, color: colors.textFaint, mt: 0.5 }}
                >
                  Auto-set from expiry date
                </Typography>
              </Box>
            </Box>
          </Stack>
        </Box>

        {/* Actions */}
        <Stack direction="row" spacing={1.5} justifyContent="flex-end" sx={{ pb: 2 }}>
          <Button
            onClick={() => navigate("/domains")}
            disabled={saving}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              px: 3,
              color: colors.textMuted,
              "&:hover": { background: colors.hover },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              px: 3.5,
              py: 1,
              color: "#fff",
              background: "linear-gradient(135deg, #3B82F6 0%, #3B82F6 100%)",
              boxShadow: "0 4px 14px rgba(59, 130, 246, 0.35)",
              "&:hover": { boxShadow: "0 8px 22px rgba(59, 130, 246, 0.45)" },
              "&.Mui-disabled": {
                color: "rgba(255,255,255,0.6)",
                background: colors.cardBorder,
              },
            }}
          >
            {saving ? "Saving…" : "Update Domain"}
          </Button>
        </Stack>
      </Stack>
    </DashboardLayout>
  );
}

export default EditDomain;
